// MARK://dhk8/admin-shell/pages/modules/ReportsPage
// Módulo 6 — Reportes. Dashboard de KPIs del día (ventas, transacciones, ticket
// promedio, propinas, comensales) sobre el read-model de dhk8-erp-reports + lista
// de reportes navegables (sales-by-hour, top productos, propinas por mesero,
// cortes de caja). Filtro de rango de fechas (default = hoy).
//
// Backend: dhk8-erp-reports (`/v1/reports/summary` + `/v1/reports/:id`) — hoy
// devuelve 0/empty contra prod (ver header de lib/clients/reports.ts). Stub
// preview muestra el dataset demo.
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, DollarSign, Receipt, RefreshCw, TrendingUp, Wallet, Landmark, CreditCard, Ban, ArrowUpRight, ArrowDownRight } from 'lucide-react'
import { listReports, getReportSummary, rebuildReportProjections, today, type ReportSummary, type DateRange, type RebuildReportsResult } from '../../lib/clients/reports.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn } from '../../lib/money.js'

/** yyyy-mm-dd n días antes de `ymd` (aritmética de calendario). */
function ymdDaysAgo(ymd: string, n: number): string {
  const d = new Date(`${ymd}T00:00:00Z`); d.setUTCDate(d.getUTCDate() - n); return d.toISOString().slice(0, 10)
}
/** Presets de temporalidad (ancla = hoy). `cmp` = rótulo de la ventana previa de
 *  igual duración (fórmula de crecimiento `(reciente/previa)−1`). */
const RANGE_PRESETS: ReadonlyArray<{ key: string; label: string; range: (today: string) => DateRange; cmp: string }> = [
  { key: '1D', label: 'Hoy', range: (t) => ({ from: t, to: t }), cmp: 'vs ayer' },
  { key: '1M', label: '30 días', range: (t) => ({ from: ymdDaysAgo(t, 29), to: t }), cmp: 'vs 30 días previos' },
  { key: '3M', label: '3M', range: (t) => ({ from: ymdDaysAgo(t, 89), to: t }), cmp: 'vs 3 meses previos' },
  { key: '6M', label: '6M', range: (t) => ({ from: ymdDaysAgo(t, 179), to: t }), cmp: 'vs 6 meses previos' },
  { key: '1Y', label: '1A', range: (t) => ({ from: ymdDaysAgo(t, 364), to: t }), cmp: 'vs año previo' },
  { key: 'YTD', label: 'Año', range: (t) => ({ from: `${t.slice(0, 4)}-01-01`, to: t }), cmp: 'vs mismo periodo previo' },
]

export default function ReportsPage() {
  const [range, setRange] = useState<DateRange>({ from: today(), to: today() })
  const [preset, setPreset] = useState<string>('1D')
  const cmpLabel = RANGE_PRESETS.find((p) => p.key === preset)?.cmp ?? 'vs periodo anterior'
  const applyPreset = (p: (typeof RANGE_PRESETS)[number]) => { setRange(p.range(today())); setPreset(p.key) }
  const list = useAsync((s) => listReports(s))
  const summary = useAsync((s) => getReportSummary(range, s), [range.from, range.to])
  const [rebuilding, setRebuilding] = useState(false)
  const [rebuildMsg, setRebuildMsg] = useState<string | null>(null)
  const [rebuildErr, setRebuildErr] = useState<string | null>(null)

  async function handleRecalc() {
    setRebuilding(true)
    setRebuildMsg(null)
    setRebuildErr(null)
    try {
      const r: RebuildReportsResult = await rebuildReportProjections(range)
      setRebuildMsg(`Recalculado: ${r.ordersReprocessed} órdenes reprocesadas.`)
      summary.reload()
      list.reload()
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setRebuildErr(/FORBIDDEN/i.test(msg) ? 'No tienes permiso para recalcular reportes.' : msg)
    } finally {
      setRebuilding(false)
    }
  }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Reportes</h1>
          <p className="adm-page__sub">KPIs del periodo + visor de los read-models del negocio</p>
        </div>
        <div className="adm-page__actions" style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <div role="group" aria-label="Periodo" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} data-testid="reports-presets">
            {RANGE_PRESETS.map((p) => (
              <button key={p.key} type="button" aria-pressed={preset === p.key}
                className={`adm-btn adm-btn--sm${preset === p.key ? ' adm-btn--primary' : ''}`}
                onClick={() => applyPreset(p)}>{p.label}</button>
            ))}
            {preset === 'custom' && <span className="adm-field__label" style={{ margin: 0, alignSelf: 'center' }}>Personalizado</span>}
          </div>
          <div className="adm-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <span className="adm-field__label" style={{ margin: 0 }}>Desde</span>
            <input type="date" className="adm-input" style={{ minHeight: 34, maxWidth: 160 }}
              value={range.from} onChange={(e) => { setRange((r) => ({ ...r, from: e.target.value })); setPreset('custom') }} aria-label="Fecha desde" />
            <span className="adm-field__label" style={{ margin: 0 }}>Hasta</span>
            <input type="date" className="adm-input" style={{ minHeight: 34, maxWidth: 160 }}
              value={range.to} onChange={(e) => { setRange((r) => ({ ...r, to: e.target.value })); setPreset('custom') }} aria-label="Fecha hasta" />
            <button
              className="adm-btn adm-btn--sm adm-btn--primary"
              onClick={handleRecalc}
              disabled={rebuilding}
              title="Reconstruye los read-models de reportes desde las órdenes del periodo (idempotente)"
              data-testid="reports-recalc-btn"
            >
              <RefreshCw size={14} className={rebuilding ? 'adm-spin' : undefined} style={{ marginRight: 6 }} />
              {rebuilding ? 'Recalculando…' : 'Recalcular'}
            </button>
          </div>
        </div>
      </div>

      {(rebuildMsg || rebuildErr) && (
        <div
          className={`adm-banner ${rebuildErr ? 'adm-banner--danger' : ''}`}
          role="status"
          data-testid="reports-recalc-result"
        >
          {rebuildErr ?? rebuildMsg}
        </div>
      )}

      {/* KPI cards */}
      {asyncState(summary.loading, summary.error, summary.reload)}
      {summary.data && <KpiCards data={summary.data} cmpLabel={cmpLabel} />}

      {/* Detail reports list */}
      <div>
        <h2 className="adm-card__title" style={{ marginBottom: 8 }}>Reportes detallados</h2>
        {asyncState(list.loading, list.error, list.reload)}
        {list.data && (
          <div className="adm-card adm-card--pad0">
            <div className="adm-list">
              {list.data.length === 0 && (
                <div className="adm-empty">
                  Sin reportes disponibles. <Link to="/dashboard" style={{ color: 'var(--pos-tint)' }}>Procesa una venta primero</Link>.
                </div>
              )}
              {list.data.map((r) => (
                <Link key={r.id} to={`/reports/${r.id}`} className="adm-row">
                  <span style={{ color: 'var(--pos-fg-tertiary)' }}><BarChart3 size={18} /></span>
                  <div className="adm-row__main">
                    <div className="adm-row__title">{r.name}</div>
                    <div className="adm-row__meta">{r.period}</div>
                  </div>
                  <span className="adm-row__meta">→</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const METHOD_LABEL: Record<string, string> = {
  CASH: 'Efectivo', CARD: 'Tarjeta', TRANSFER: 'Transferencia', VOUCHER: 'Vale', OTHER: 'Otro',
}

/** Δ% vs periodo anterior; null si el periodo anterior fue 0 (evita div/0). */
function deltaPct(cur: number, prev: number): number | null {
  if (!prev) return null
  return ((cur - prev) / prev) * 100
}

function DeltaLine({ pct, cmpLabel }: { pct: number | null; cmpLabel: string }) {
  if (pct === null) {
    return <div style={{ marginTop: 2, fontSize: 'var(--pos-text-xs)', color: 'var(--pos-fg-tertiary)' }}>— {cmpLabel}</div>
  }
  const up = pct >= 0
  const color = up ? 'var(--pos-success, #2e9e5b)' : 'var(--pos-danger, #d64545)'
  const Icon = up ? ArrowUpRight : ArrowDownRight
  return (
    <div style={{ marginTop: 2, fontSize: 'var(--pos-text-xs)', color, display: 'flex', alignItems: 'center', gap: 3 }}>
      <Icon size={12} />{Math.abs(pct).toFixed(1)}% {cmpLabel}
    </div>
  )
}

function KpiCards({ data, cmpLabel }: { data: ReportSummary; cmpLabel: string }) {
  const isEmpty = data.salesCents === 0 && data.tickets === 0
  if (isEmpty) {
    return (
      <div className="adm-card">
        <div className="adm-empty" data-testid="reports-empty-state">
          Aún no hay ventas en este periodo. <Link to="/dashboard" style={{ color: 'var(--pos-tint)' }}>Procesa una venta primero</Link> y los KPIs aparecerán aquí.
        </div>
      </div>
    )
  }
  const totalPm = data.paymentMethods.reduce((a, p) => a + p.amountCents, 0)
  return (
    <>
      <div className="adm-grid adm-grid--wide" data-testid="reports-kpis">
        <Kpi icon={<DollarSign size={18} />} label="Ventas" value={mxn(data.salesCents)} hint={data.period}
          sub={<DeltaLine pct={deltaPct(data.salesCents, data.prevSalesCents)} cmpLabel={cmpLabel} />} />
        <Kpi icon={<Receipt size={18} />} label="Tickets" value={String(data.tickets)} hint={data.period}
          sub={<DeltaLine pct={deltaPct(data.tickets, data.prevTickets)} cmpLabel={cmpLabel} />} />
        <Kpi icon={<TrendingUp size={18} />} label="Ticket promedio" value={mxn(data.avgTicketCents)} hint={data.period} />
        <Kpi icon={<Landmark size={18} />} label="IVA cobrado" value={mxn(data.taxCents)} hint={data.period} />
        <Kpi icon={<Wallet size={18} />} label="Propinas" value={mxn(data.tipsCents)} hint={data.period} />
      </div>

      <div className="adm-grid" style={{ marginTop: 12 }} data-testid="reports-extra">
        {/* Métodos de pago — datos REALES (PosPayment CAPTURED) */}
        <div className="adm-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--pos-fg-tertiary)', marginBottom: 10 }}>
            <CreditCard size={18} />
            <span style={{ fontSize: 'var(--pos-text-xs)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.06em' }}>Métodos de pago</span>
          </div>
          {data.paymentMethods.length === 0 ? (
            <div className="adm-empty">Sin pagos en el periodo.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="reports-payment-methods">
              {data.paymentMethods.map((p) => {
                const pct = totalPm > 0 ? (p.amountCents / totalPm) * 100 : 0
                return (
                  <div key={p.method}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--pos-text-sm)' }}>
                      <span>{METHOD_LABEL[p.method] ?? p.method} <span style={{ color: 'var(--pos-fg-tertiary)' }}>· {p.count}</span></span>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}><strong>{mxn(p.amountCents)}</strong> <span style={{ color: 'var(--pos-fg-tertiary)' }}>{pct.toFixed(0)}%</span></span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: 'var(--pos-surface-2, rgba(127,127,127,.15))', marginTop: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: 'var(--pos-tint, #2c56f0)' }} />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Cancelaciones y descuentos — control de mermas */}
        <div className="adm-card" data-testid="reports-voids-discounts">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--pos-fg-tertiary)', marginBottom: 10 }}>
            <Ban size={18} />
            <span style={{ fontSize: 'var(--pos-text-xs)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.06em' }}>Cancelaciones y descuentos</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 'var(--pos-text-sm)' }}>Órdenes canceladas</span>
              <span style={{ fontFamily: 'var(--font-display, var(--font-sans))' }}><strong>{data.cancelledCount}</strong> <span style={{ color: 'var(--pos-fg-tertiary)', fontSize: 'var(--pos-text-xs)' }}>{mxn(data.cancelledCents)}</span></span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 'var(--pos-text-sm)' }}>Órdenes con descuento</span>
              <span style={{ fontFamily: 'var(--font-display, var(--font-sans))' }}><strong>{data.discountedOrders}</strong> <span style={{ color: 'var(--pos-fg-tertiary)', fontSize: 'var(--pos-text-xs)' }}>{mxn(data.discountCents)}</span></span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function Kpi({ icon, label, value, hint, sub }: { icon: React.ReactNode; label: string; value: string; hint: string; sub?: React.ReactNode }) {
  return (
    <div className="adm-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--pos-fg-tertiary)' }}>
        {icon}
        <span style={{ fontSize: 'var(--pos-text-xs)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</span>
      </div>
      <div style={{ marginTop: 6, fontFamily: 'var(--font-display, var(--font-sans))', fontSize: 'var(--pos-text-2xl, 1.5rem)', fontWeight: 700 }}>{value}</div>
      <div style={{ marginTop: 2, fontSize: 'var(--pos-text-xs)', color: 'var(--pos-fg-tertiary)' }}>{hint}</div>
      {sub}
    </div>
  )
}
